const API_URL = process.env.REACT_APP_API_URL;
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry

// Get token expiry from JWT
function getTokenExpiry(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp * 1000; // Convert to milliseconds
    } catch (err) {
        console.error('Failed to parse token:', err);
        return 0;
    }
}

// Check if token needs refresh
function shouldRefreshToken() {
    const token = localStorage.getItem('adminToken');
    if (!token) return false;

    const expiry = getTokenExpiry(token);
    const now = Date.now();
    return (expiry - now) < TOKEN_REFRESH_THRESHOLD;
}

// Refresh the token
async function refreshAccessToken() {
    const refreshToken = localStorage.getItem('adminRefreshToken');
    if (!refreshToken) return false;

    try {
        const response = await fetch(`${API_URL}/admin/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken })
        });

        if (response.ok) {
            const data = await response.json();
            localStorage.setItem('adminToken', data.token);
            return true;
        }
    } catch (err) {
        console.error('Token refresh failed:', err);
    }
    return false;
}

// Clear all auth data
function clearTokens() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
}

/**
 * GATE 0 for every live refetch (LIV, §9).
 *
 * Answers "may a background refetch go out?" - renewing the session first if it
 * only needs renewing.
 *
 * ⛔ WHY IT REFRESHES RATHER THAN JUST CHECKING. The first version tested the ID
 * token's expiry and skipped. On PROD an admin ID token lives 10 MINUTES while
 * the refresh token lives 12 hours, and `apiFetch` renews proactively on every
 * call - so that version went quiet ten minutes after a tab's last API call and
 * stayed quiet until somebody clicked, while the handlers already on the same
 * socket (ProjectDataContext, the Dashboard's issues_updated, TemplateEditor)
 * self-healed on every message. Preview tokens live 60 minutes, so no smoke
 * pass would ever have shown it. Found by Fable's build review, 2026-09-03;
 * Bill's call to fix it this way.
 *
 * ⚠️ `refreshAccessToken` returns a boolean and NEVER navigates. That is the
 * whole point of gate 0: `apiFetch` answers a 401 by clearing the tokens and
 * assigning `window.location.href`, and until live updates existed that could
 * only run behind a user action. A message must not be able to redirect an idle
 * tab - so when the refresh fails, this returns false and the refetch simply
 * does not happen. The next real user action still takes the ordinary 401 path.
 *
 * ⚠️ SINGLE-FLIGHT. Callers arrive per WebSocket message, and messages arrive in
 * bursts; with a lapsed token every one of them inside the first renewal's round
 * trip would otherwise start its own POST to the refresh route (Fable, round 2).
 * One renewal is shared by everyone who asks while it is in flight, and the
 * slot is cleared when it settles so the NEXT lapse renews again.
 *
 * ⚠️ Callers put this at the point of REFETCH - inside the debounced runner,
 * after the type gate and gate 3 - never per message in the socket handler.
 * There it renews for messages the page ignores (`audit_updated` reaches every
 * admin tab for every audit batch anywhere) and it misses the deferred-flush
 * path entirely, which reaches `apiFetch` and its redirect with no gate at all.
 */
let renewalInFlight = null;

async function adminSessionUsable() {
    const token = localStorage.getItem('adminToken');
    if (token && getTokenExpiry(token) > Date.now()) return true;
    if (!renewalInFlight) {
        renewalInFlight = refreshAccessToken().finally(() => { renewalInFlight = null; });
    }
    return renewalInFlight;
}

export { shouldRefreshToken, refreshAccessToken, clearTokens, getTokenExpiry, adminSessionUsable };
